import { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { QuoteTimeSeriesPoint } from '../../types';

interface QuoteTimeSeriesChartProps {
  data: QuoteTimeSeriesPoint[];
  mode: 'count' | 'value';
  granularity: 'daily' | 'weekly' | 'monthly';
}

const STATUS_COLORS = {
  approved: '#22c55e',  // green-500
  pending: '#f59e0b',   // amber-500
  rejected: '#ef4444'   // red-500
};

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);

const formatInteger = (value: number): string =>
  value.toLocaleString('pt-BR');

const formatTooltipValue = (value: number, mode: 'count' | 'value'): string => {
  if (mode === 'count') {
    return `${formatInteger(value)} ${value === 1 ? 'orçamento' : 'orçamentos'}`;
  }
  return formatCurrency(value);
};

export function QuoteTimeSeriesChart({ data, mode, granularity }: QuoteTimeSeriesChartProps) {
  const safeData = Array.isArray(data) ? data : [];

  const chartData = useMemo(() => {
    return safeData.map((point) => ({
      periodLabel: point.periodLabel,
      ...(mode === 'count'
        ? {
            Aprovados: point.approvedCount,
            Pendentes: point.pendingCount,
            Rejeitados: point.rejectedCount
          }
        : {
            Aprovados: point.approvedValue,
            Pendentes: point.pendingValue,
            Rejeitados: point.rejectedValue
          })
    }));
  }, [safeData, mode]);

  const title =
    granularity === 'daily' ? 'Evolução Diária' :
    granularity === 'weekly' ? 'Evolução Semanal' :
    'Evolução Mensal';
  const description = mode === 'count'
    ? 'Quantidade de orçamentos por status'
    : 'Valor dos orçamentos por status';

  if (safeData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            Nenhum dado disponível para o período selecionado
          </div>
        </CardContent>
      </Card>
    );
  }

  const yAxisFormatter = mode === 'count' ? formatInteger : formatCurrency;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="periodLabel"
              className="text-xs"
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
            />
            <YAxis
              className="text-xs"
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
              tickFormatter={yAxisFormatter}
              allowDecimals={mode !== 'count'}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--background))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px'
              }}
              formatter={(value: number | undefined) =>
                value != null ? formatTooltipValue(value, mode) : ''
              }
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="Aprovados"
              name="Aprovados"
              stroke={STATUS_COLORS.approved}
              strokeWidth={2}
              dot={{ fill: STATUS_COLORS.approved, strokeWidth: 2 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="Pendentes"
              name="Pendentes"
              stroke={STATUS_COLORS.pending}
              strokeWidth={2}
              dot={{ fill: STATUS_COLORS.pending, strokeWidth: 2 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="Rejeitados"
              name="Rejeitados"
              stroke={STATUS_COLORS.rejected}
              strokeWidth={2}
              dot={{ fill: STATUS_COLORS.rejected, strokeWidth: 2 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
